import React, { useState, useCallback } from 'react'
import { useLocalStorage } from '../hooks/useLocalStorage'
import Cropper from 'react-easy-crop'
import { Camera, X, Check, Upload } from 'lucide-react'

interface ProfileImageUploaderProps {
  userId: string
  onImageUpdate: () => void
}

const ProfileImageUploader: React.FC<ProfileImageUploaderProps> = ({ userId, onImageUpdate }) => {
  const [profileImage, setProfileImage] = useLocalStorage<string | null>(`profileImage-${userId}`, null)
  const [isUploading, setIsUploading] = useState(false)
  const [imageSrc, setImageSrc] = useState<string | null>(null)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)
  
  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0]
      const reader = new FileReader()
      reader.addEventListener('load', () => {
        setImageSrc(reader.result as string)
        setIsUploading(true)
      })
      reader.readAsDataURL(file)
    }
  }
  
  const onCropComplete = useCallback((croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])
  
  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image()
      image.addEventListener('load', () => resolve(image))
      image.addEventListener('error', error => reject(error))
      image.src = url
    })
  
  const getCroppedImg = async (
    imageSrc: string,
    pixelCrop: any
  ): Promise<string> => {
    const image = await createImage(imageSrc)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    
    if (!ctx) {
      throw new Error('Could not get canvas context')
    }
    
    // Set canvas size to the desired output size
    canvas.width = pixelCrop.width
    canvas.height = pixelCrop.height
    
    // Draw the cropped image
    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    )
    
    // As Base64 string
    return canvas.toDataURL('image/jpeg', 0.9)
  }
  
  const saveCroppedImage = async () => {
    if (!imageSrc || !croppedAreaPixels) return
    
    try {
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels)
      setProfileImage(croppedImage)
      setIsUploading(false)
      setImageSrc(null)
      onImageUpdate()
    } catch (e) {
      console.error('Error cropping image:', e)
    }
  }
  
  const cancelCrop = () => {
    setIsUploading(false)
    setImageSrc(null)
  }
  
  const removeProfileImage = () => {
    setProfileImage(null)
    onImageUpdate()
  }
  
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Profile Picture</h3>
        {profileImage && (
          <button
            onClick={removeProfileImage}
            className="text-red-600 dark:text-red-400 text-sm hover:underline flex items-center"
          >
            <X size={16} className="mr-1" />
            Remove
          </button>
        )}
      </div>
      
      {isUploading && imageSrc ? (
        <div className="bg-[var(--color-background)] dark:bg-[#1f1f1f] rounded-lg p-4 mb-4">
          <div className="relative h-64 w-full mb-4">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
            />
          </div>
          
          <div className="mb-4">
            <label htmlFor="zoom" className="block text-sm font-medium mb-1">
              Zoom: {zoom.toFixed(1)}x
            </label>
            <input
              type="range"
              id="zoom"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="w-full h-2 bg-[var(--color-accent)] rounded-lg appearance-none cursor-pointer"
            />
          </div>
          
          <div className="flex space-x-2">
            <button
              onClick={saveCroppedImage}
              className="btn btn-primary flex items-center"
            >
              <Check size={18} className="mr-2" />
              Save
            </button>
            <button
              onClick={cancelCrop}
              className="btn btn-outline flex items-center"
            >
              <X size={18} className="mr-2" />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center space-x-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-[var(--color-background)] dark:bg-[#1f1f1f] flex items-center justify-center">
              {profileImage ? (
                <img 
                  src={profileImage} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              ) : (
                <Camera size={32} className="text-[var(--color-accent)]" />
              )}
            </div>
          </div>
          
          <div>
            <label className="btn btn-primary flex items-center cursor-pointer">
              <Upload size={18} className="mr-2" />
              {profileImage ? 'Change Picture' : 'Upload Picture'}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onFileChange}
              />
            </label>
            <p className="text-sm text-[var(--color-accent)] mt-2">
              Recommended: Square image, at least 200x200 pixels
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfileImageUploader